import { useMemo, FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { getDate } from 'utils/convertString';
import { useStore } from 'hooks/useRootStore';
import { useDebounce } from 'hooks/useDebounce';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { Button } from 'components/Core/Button';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { StatusCell } from 'components/Core/StatusCell';
import { AvatarIcon } from 'components/Core/AvatarIcon';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import SelectComponent from 'components/Inputs/SelectComponent';
import { ISelectStyleProps } from 'components/Inputs/SelectComponent/types';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { AppLoader } from 'components/Core/AppLoader/AppLoader';
import { IParsersList } from 'stores/parsers/types';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { ReactComponent as ParsersIcon } from 'assets/navbar/navbar_sync.svg';
import { parsersTableTranslations } from './translations';
import { syncTypeOptions } from './constants';
import { ParsersTableFiltersForm, ParsersTableTransFields } from './types';
import styles from './parsersTable.module.scss';

const selectStyles: ISelectStyleProps = {
  containerStyles: {
    marginBottom: 0,
    width: 200,
    backgroundColor: 'white',
    borderRadius: 32,
  },
  controlStyles: {
    background: 'white',
    borderRadius: 32,
  },
};

const ParsersTable: FC = () => {
  const {
    parsersStore: { parsersList, changeParsersStatus, resyncParsers, getParsersList },
    mainStore: { user },
    alertStore: { showAlert },
  } = useStore();

  const [filtered, setfiltered] = useState<IParsersList[]>([]);

  const navigate = useNavigate();

  const { t } = useMyTranslations<ParsersTableTransFields>([parsersTableTranslations.name]);

  const { mutate, isLoading: isFetching } = useMutation(getParsersList, {
    mutationKey: 'getParsersList',
    retry: false,
  });

  const { mutate: resyncMutate, isLoading } = useMutation(resyncParsers, {
    mutationKey: 'resyncParsers',
    onSuccess: () => {
      showAlert(t('resyncParsersSuccess'), 'success');
    },
  });

  useEffect(() => {
    mutate();
  }, [user?.selectedCountryCode]);

  const useFormMethods = useForm<ParsersTableFiltersForm>();

  const { watch, getValues } = useFormMethods;
  const searchD = useDebounce(watch('search'));

  useEffect(() => {
    let filtered = parsersList || [];
    const typeId = getValues('type')?.id;
    if (typeId || searchD) {
      filtered = filtered.reduce((acc, item) => {
        if (item.brand) {
          const isfilteredById = typeId ? item.type === typeId : true;
          if (isfilteredById && item.brand.name.toLowerCase().includes(searchD?.toLowerCase() || '')) {
            acc.push(item);
          }
        }
        return acc;
      }, [] as IParsersList[]);
    }
    setfiltered(filtered);
  }, [searchD, parsersList, watch('type')]);

  const onEditClick = useCallback((item: IParsersList) => {
    navigate(`${item.id}`);
  }, []);

  const columns: Column<IParsersList>[] = useMemo(
    () => [
      {
        Header: t('brand'),
        id: 'brand',
        Cell: ({
          row: {
            original,
            original: {
              brand: { name, logoUrl },
              completeStatus,
            },
          },
        }: CellProps<IParsersList>) => (
          <button
            className={styles.avatar}
            onClick={() => onEditClick(original)}
            disabled={isLoading || completeStatus === 'in-progress'}>
            <AvatarIcon avatar={logoUrl} name='brand image' size={56} styleProps={{ borderRadius: 8 }} />
            <p className={styles.name}>{name}</p>
          </button>
        ),
        accessor: ({ brand: { name } }) => name,
        width: 10,
      },
      {
        Header: t('type'),
        id: 'type',
        accessor: ({ type }) => `${t(type)}`,
        width: 4,
      },
      {
        Header: t('lastSyncDate'),
        id: 'lastSyncDate',
        Cell: ({
          row: {
            original: { lastSyncDate },
          },
        }: CellProps<IParsersList>) => (
          <p className={styles.date}>{lastSyncDate ? getDate(lastSyncDate, true) : '—'}</p>
        ),
        accessor: ({ lastSyncDate }) => (lastSyncDate ? +new Date(lastSyncDate) : 0),
        width: 4,
      },
      {
        Header: t('completeStatus'),
        id: 'completeStatus',
        accessor: ({ completeStatus }) => completeStatus,
        width: 4,
      },
      {
        Header: t('nextStartDate'),
        id: 'nextStartDate',
        Cell: ({
          row: {
            original: { nextStartDate },
          },
        }: CellProps<IParsersList>) => (
          <p className={styles.date}>{nextStartDate ? getDate(nextStartDate, true) : '—'}</p>
        ),
        accessor: ({ nextStartDate }) => (nextStartDate ? +new Date(nextStartDate) : 0),
        width: 4,
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status, completeStatus },
          },
        }: CellProps<IParsersList>) => (
          <StatusCell
            id={id}
            status={status}
            changeStatus={changeParsersStatus}
            disabled={isLoading || completeStatus === 'in-progress'}
          />
        ),
        accessor: ({ status }) => status,
        width: 3,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({
          row: {
            original,
            original: { id, completeStatus, status },
          },
        }: CellProps<IParsersList>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button
                  className={`${styles.editBtn}`}
                  onClick={() => onEditClick(original)}
                  disabled={isLoading || completeStatus === 'in-progress'}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message={t('edit')}
              place='top'
            />
            <ComponentWithTooltip
              component={
                <button
                  className={`${styles.editBtn}`}
                  onClick={() => resyncMutate(id)}
                  disabled={isLoading || completeStatus === 'in-progress' || status !== 'Active'}>
                  <ParsersIcon width={16} height={16} />
                </button>
              }
              name='syncIcon'
              message={t('resync')}
              place='top'
            />
          </div>
        ),
        width: 3,
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line
    [isLoading, filtered]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      <FormProvider {...useFormMethods}>
        <div className={styles.search}>
          <SelectComponent
            name='type'
            placeholder={t('all')}
            options={syncTypeOptions.map((item) => ({
              id: item,
              name: t(item),
            }))}
            optionLabel='name'
            optionValue='id'
            styleProps={selectStyles}
          />
          <ControlledInput name='search' classNameProp={styles.searchInput} placeholder={t('search')} isRemoveMargin />
          <Button title={t('addBtn')} onClick={() => navigate('/add-parser')} stylesProp={{ padding: '8px 36px' }} />
        </div>
      </FormProvider>
      {isFetching ? (
        <AppLoader />
      ) : parsersList && parsersList.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...filtered]}
          simpleList
          isPaginated={parsersList.length > PAGE_SIZE}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
};

export default observer(ParsersTable);
